import type { TImportAcceptedKeys, TImportTitles } from "components/ImportData/import_data";
import type { TImportDataDictionary } from "components/ImportData/type";
import MappingTable from "components/shared/MappingTable";
import MessageBlock from "components/shared/MessageBlock";
import { getLegacyMilestoneBaseColumn } from "containers/TermFormContainer/ChannelsStep/milestoneBaseColumnUtils";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchBeneficiar } from "store/beneficiaryStore";
import type { ITerm } from "store/managementStore";
import { useAppDispatch } from "store/store";
import { fetchTerm } from "store/termStore";
import { fetchTitle } from "store/titleStore";

import type { TExampleData, TFileData } from "..";
import {
    applyBulkUpdateMappingToLoadedData,
    getSavedBulkUpdateMapping,
    isBulkUpdateMappingInitial,
    saveBulkUpdateMapping,
    suggestBulkUpdateMapping,
} from "../mappingUtils";
import StepTitle from "../StepTitle";
import StepWrapper from "../StepWrapper";
import styles from "./style.module.scss";

interface IMappingStep {
    loadedData: TFileData | undefined;
    fileTitles: string[];
    /**
     * Data from the file.
     */
    setLoadedData: Dispatch<SetStateAction<TFileData | undefined>>;
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    /**
     * Our accepted keys for the element.
     */
    crealoKeys: TImportTitles | undefined;
    /**
     * Columns matched.
     */
    selectedValues: Record<string, string>;
    /**
     * Sets columns matched.
     */
    setSelectedValues: Dispatch<SetStateAction<Record<string, string>>>;
    /**
     * Sets value of first element data for the examples in the next step.
     */
    setExampleData: Dispatch<SetStateAction<TExampleData | undefined>>;
    element: TImportDataDictionary | undefined;
    exampleData: TExampleData | undefined;
    repeatedKeys: string[];
    acceptedKeys: TImportAcceptedKeys | undefined;
}

/**
 * Step for mapping columns of their file with our values.
 */
const MappingStep = ({
    loadedData,
    setLoadedData,
    fileTitles,
    activeStep,
    setActiveStep,
    crealoKeys,
    selectedValues,
    setSelectedValues,
    setExampleData,
    element,
    exampleData,
    repeatedKeys,
    acceptedKeys,
}: IMappingStep) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const columnNames = [
        t("BulkUpdates.MappingStep.Column1"),
        t("BulkUpdates.MappingStep.Column2"),
        t("BulkUpdates.MappingStep.Column3"),
    ];
    const [blockButton, setBlockButton] = useState(false);
    const [blockMessage, setBlockMessage] = useState("");
    const [idErrorMessage, setIdErrorMessage] = useState(false);

    // On first render, save id as "Ignore" in loadedData to force them to choose a column for it.
    // All other columns have no match from our keys in loadedData (aka loadedData = {"Prénom": XX, ...., id: "Ignore"})
    useEffect(() => {
        if (!loadedData || !loadedData.length || loadedData[0]["_id"]) return;
        setLoadedData((prevData) => {
            if (!prevData) return [];
            return prevData.map((item) => ({
                ...item,
                ["_id"]: "Ignore",
            }));
        });
    }, []);

    useEffect(() => {
        if (!loadedData || !crealoKeys || !acceptedKeys || !element || !fileTitles.length) {
            return;
        }

        if (!isBulkUpdateMappingInitial(fileTitles, selectedValues)) {
            return;
        }

        const suggestedMapping = suggestBulkUpdateMapping({
            fileTitles,
            crealoKeys,
            acceptedKeys,
            savedMapping: getSavedBulkUpdateMapping(element),
            repeatedKeys,
        });

        setSelectedValues(suggestedMapping);
        setLoadedData(applyBulkUpdateMappingToLoadedData(loadedData, fileTitles, suggestedMapping, repeatedKeys));
    }, [acceptedKeys, crealoKeys, element, fileTitles, loadedData, repeatedKeys, selectedValues]);

    const handleMappingValidated = () => {
        if (element) {
            saveBulkUpdateMapping(element, selectedValues);
        }
    };

    const handleBlockTrigger = () => {
        setBlockButton(true);
    };

    const getExampleData = useCallback(() => {
        if (!loadedData || !element) return;
        switch (element) {
            case "beneficiars":
                dispatch(fetchBeneficiar(loadedData[0]["_id"])).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        setExampleData(resp.payload);
                    } else if (resp.meta.requestStatus === "rejected") {
                        setIdErrorMessage(true);
                    }
                });
                break;
            case "titles":
                dispatch(fetchTitle(loadedData[0]["_id"])).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        setExampleData(resp.payload);
                    } else if (resp.meta.requestStatus === "rejected") {
                        setIdErrorMessage(true);
                    }
                });
                break;
            case "terms": {
                dispatch(fetchTerm(loadedData[0]["_id"])).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        const term = structuredClone(resp.payload) as ITerm;
                        // Formatting channelData makes it easier to handle in next step.
                        const newChannelData = term.channelData.map((channel) => {
                            // Thresholds are stored in the unit of the contract milestone column: CA in cents.
                            const milestoneBaseColumn =
                                channel.milestoneBaseColumn ??
                                channel.calculationBaseColumn ??
                                getLegacyMilestoneBaseColumn({ type: channel.channelType });

                            if (milestoneBaseColumn !== "quantity") {
                                const data = JSON.parse(channel.data).map((milestone) => ({
                                    ...milestone,
                                    quantity: milestone.quantity / 100,
                                }));
                                return { ...channel, data };
                            } else {
                                return { ...channel, data: JSON.parse(channel.data) };
                            }
                        });
                        term.channelData = newChannelData;
                        setExampleData(term);
                    } else if (resp.meta.requestStatus === "rejected") {
                        setIdErrorMessage(true);
                    }
                });
                break;
            }
        }
    }, [loadedData]);

    /**
     * Checks if ID column has been matched (aka is different than Ignore).
     */
    useEffect(() => {
        if (!loadedData) return;

        const loadedDataKeys = Object.keys(loadedData[0]);

        const blockForId = () => {
            if (!loadedData[0]["_id"] || loadedData[0]["_id"] == "Ignore") return t("BulkUpdates.MappingStep.IDInfo");
            return "";
        };

        // If they add a channelMilestone or channelPercentage value, they have to add channelId.
        const blockForTermChannel = () => {
            if (element !== "terms" || loadedData[0]["_channelId"]) return "";
            if (loadedDataKeys.some((key) => key.includes("channelMilestone") || key.includes("channelPercentage"))) {
                return t("BulkUpdates.MappingStep.ChannelIDInfo");
            }
            return "";
        };

        const blockForTitleChannel = () => {
            if (element !== "titles") return "";

            // Opening cumulatives / subscription coefficient only require channelId
            const openingCumulativeKeys = [
                "_cumulativeSales",
                "_quantity",
                "_turnover",
                "_turnover_1",
                "_turnover_2",
                "_channelSubscriptionCoefficient",
            ];
            if (openingCumulativeKeys.some((key) => loadedDataKeys.includes(key))) {
                return loadedDataKeys.includes("_channelId") ? "" : t("BulkUpdates.MappingStep.ChannelIDInfo");
            }

            // channelISBN/channelPrice require all three together
            const titleChannelKeys = ["_channelISBN", "_channelPrice", "_channelId"];
            const channelKeyChosen = titleChannelKeys.some((key) => loadedDataKeys.includes(key));
            const allKeysSelected = titleChannelKeys.every((key) => loadedDataKeys.includes(key));
            if (channelKeyChosen && !allKeysSelected) {
                return t("BulkUpdates.MappingStep.TitleChannelInfo");
            }

            return "";
        };

        if (blockForId() || blockForTermChannel() || blockForTitleChannel()) {
            setBlockMessage(blockForId() + " " + blockForTermChannel() + blockForTitleChannel());
        } else {
            setBlockMessage("");
            setBlockButton(false);

            // Once they've selected id column, fetch element data that corresponds to first ID of their file
            // Only do it once so as not to fetch each time they make a change.
            if (typeof +loadedData[0]["_id"] === "number" && !exampleData) {
                getExampleData();
            }
        }
    }, [loadedData]);

    return (
        <StepWrapper
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            conditionToBlock={blockButton || !exampleData}
            handleTrigger={handleBlockTrigger}
            blockMessage={blockMessage}
            extraAction={handleMappingValidated}
        >
            <div className={styles["mapping-step-container"]}>
                <StepTitle
                    title={t("BulkUpdates.MappingStep.Title")}
                    subtitle={t("BulkUpdates.MappingStep.Subtitle")}
                />
                {idErrorMessage && (
                    <MessageBlock
                        type="error"
                        text={t("BulkUpdates.MappingStep.IDError")}
                        style={{ maxWidth: "1000px", margin: "0 auto" }}
                    />
                )}
                <MappingTable
                    loadedData={loadedData}
                    setLoadedData={setLoadedData}
                    options={crealoKeys}
                    columnsToMatch={fileTitles}
                    selectedValues={selectedValues}
                    setSelectedValues={setSelectedValues}
                    columnNames={columnNames}
                    repeatedKeys={repeatedKeys}
                />
            </div>
        </StepWrapper>
    );
};

export default MappingStep;
